import { ISnippetSourceBundle, ISnippetSourceDetails } from '@/types/gigiChat/ConversationSettings'
import { IFilteredSnippets } from '@/types/gigiChat/Snippets'

export const filterSnippetSourcesByUserGroups = (snippetSources: ISnippetSourceDetails, userGroups: Array<string>): IFilteredSnippets => {
    // Convert userGroups to a Set for faster lookup
    const userGroupsSet = new Set(userGroups)

    //get all allowed group snippets
    const filteredAllowedSnippets = snippetSources.bundle.filter(
        (item) => !item.adGroupIds.length || item.adGroupIds.some((adGroupId) => userGroupsSet.has(adGroupId)),
    )

    // Filter data
    const filteredData = snippetSources.data.filter((snippetSource) =>
        filteredAllowedSnippets.some((item) => item.snippetSources.indexOf(snippetSource.snippetSource) !== -1),
    )

    // Extract the allowed snippet source enums
    const allowed_snippet_source_enums = filteredData.map((snippetSource) => snippetSource.snippetSource)

    // Filter bundles
    const filteredBundles = snippetSources.bundle
        .filter((bundle) => !bundle.adGroupIds.length || bundle.adGroupIds.some((adGroupId) => userGroupsSet.has(adGroupId)))
        .map((bundle) => ({
            bundle: bundle.bundleName,
            enums: bundle.snippetSources.filter((snippetSource) => allowed_snippet_source_enums.includes(snippetSource)),
        }))

    return {
        allowed_snippet_source_enums,
        allowed_bundle_configurations: filteredBundles,
    }
}

export const getFilterSnippetSources = (
    userGroups: Array<string>,
    snippetSourceDetails: ISnippetSourceDetails,
): Array<ISnippetSourceBundle> => {
    const availableBundle: Array<ISnippetSourceBundle> = []

    for (const bundle of snippetSourceDetails.bundle) {
        if (
            (bundle.adGroupIds.length === 0 || bundle.adGroupIds.some((adGroupId) => userGroups.includes(adGroupId))) &&
            bundle.bundleName !== 'DocAnalysis'
        ) {
            availableBundle.push(bundle)
        }
    }

    return availableBundle
}

export const getSnippetSourcesForBundle = (
    bundleName: string,
    userGroups: Array<string>,
    snippetSourceDetails: ISnippetSourceDetails,
): Array<string> => {
    // Find the bundle by name
    const bundle = snippetSourceDetails.bundle.find((bundle) => bundle.bundleName === bundleName)

    if (!bundle) {
        return [] // If the bundle is not found, return an empty array
    }

    // Filter `snippetSources` to include only those that belong to the given user groups
    const filteredSnippetSources = bundle.snippetSources.filter((snippetSourceId) => {
        // Find the snippet source in the data
        const snippetSourceData = snippetSourceDetails.data.find((snippet) => snippet.snippetSource === snippetSourceId)

        if (!snippetSourceData) {
            return false // If the snippet source is not found in the data, exclude it
        }

        // Check if the `adGroupIds` of the `snippetSource` match those in `userGroups`
        return snippetSourceData.adGroupIds.length === 0 || snippetSourceData.adGroupIds.some((adGroupId) => userGroups.includes(adGroupId))
    })

    return filteredSnippetSources
}

export const getSnippetSourcesId = (snippetSources: ISnippetSourceDetails) => {
    const data = snippetSources.data
        .map((item) => {
            if (item.adGroupIds.length) {
                return item.adGroupIds
            }
        })
        .flat()
        .filter((n) => n)

    const bundle = snippetSources.bundle
        .map((item) => {
            if (item.adGroupIds.length) {
                return item.adGroupIds
            }
        })
        .flat()
        .filter((n) => n)

    return [...new Set([...data, ...bundle])]
}
